export interface OidcConfig {
  issuer: string;
  clientId: string;
  redirectUri: string;
  postLogoutRedirectUri?: string;
  scopes?: string[];
  audience?: string;
  /** Where login transactions (state, nonce, verifier) are kept. Defaults to `sessionStorage`. */
  storage?: Storage;
  fetch?: typeof fetch;
  /** Tolerance when validating `exp`/`iat` claims (default 60s). */
  clockSkewSeconds?: number;
  /** Refresh this many seconds before the access token expires (default 60s). */
  refreshLeewaySeconds?: number;
  /**
   * Redirect URI used by silent session restoration (`prompt=none` in a hidden iframe). Must be registered on the
   * client and served by `/auth/silent-callback.html`. Empty disables the iframe and leaves only the top-level
   * `prompt=none` redirect (ADR-0022).
   */
  silentRedirectUri?: string;
  /** How long the hidden iframe may take before silent restoration is abandoned (default 6000ms). */
  silentTimeoutMs?: number;
}

export interface TokenSet {
  accessToken: string;
  idToken?: string;
  refreshToken?: string;
  /** Epoch milliseconds. */
  expiresAt: number;
  scope?: string;
  tokenType: string;
}

export interface IdTokenClaims {
  sub: string;
  iss?: string;
  aud?: string | string[];
  exp?: number;
  iat?: number;
  name?: string;
  preferred_username?: string;
  email?: string;
  zagru_tenant?: string;
  zagru_roles?: string[];
  zagru_permissions?: string[];
  [k: string]: unknown;
}

export interface OidcDiscoveryDocument {
  issuer: string;
  authorization_endpoint: string;
  token_endpoint: string;
  end_session_endpoint?: string;
  jwks_uri?: string;
  userinfo_endpoint?: string;
  [k: string]: unknown;
}

export interface LoginOptions {
  returnTo?: string;
  /** `none` asks the identity provider to answer from its SSO session without showing any page. */
  prompt?: string;
  loginHint?: string;
  /** The viewer's language (`ui_locales`), so the identity pages open in the language the app was in. */
  uiLocales?: string;
  /**
   * The workspace (tenant code) the sign-in is for, sent as `zagru_tenant`: the identity pages show that tenant's
   * name, logo and colour (ADR-0042). Unknown to the provider's protocol, so it is ignored everywhere else.
   */
  tenantHint?: string;
}

export interface CallbackResult {
  returnTo?: string;
  tokens: TokenSet;
}

export type OidcListener = (tokens: TokenSet | null, claims: IdTokenClaims | null) => void;

export interface OidcClient {
  /** GET `{issuer}/.well-known/openid-configuration`, cached. */
  discover(): Promise<OidcDiscoveryDocument>;
  /** Starts Authorization Code + PKCE: stores the transaction in storage and navigates to the authorize URL. */
  login(options?: LoginOptions): Promise<void>;
  /** Signs in against the token endpoint without showing the identity provider hosted login screen. */
  loginWithPassword(username: string, password: string): Promise<TokenSet>;
  /** Completes the flow from the redirect URL: verifies state, exchanges the code, validates the id_token. */
  handleCallback(url?: string): Promise<CallbackResult>;
  /**
   * Refresh-token grant; single-flight. When the refresh token is gone or rejected, falls back to silent
   * restoration from the identity provider's SSO session before giving up and clearing the session.
   */
  refresh(): Promise<TokenSet | null>;
  /**
   * Restores a session without user interaction: a `prompt=none` authorization request in a hidden iframe,
   * answered from the identity provider's SSO cookie. Resolves the new tokens, or rejects with an
   * {@link OidcError} (`login_required`, `interaction_required`, `client.silent_timeout`, `client.silent_unavailable`).
   */
  signInSilently(): Promise<TokenSet>;
  /** Drops the in-memory session without contacting the identity provider (another tab signed out). */
  clearLocalSession(): void;
  /** Access token, refreshed when within the leeway of expiry. */
  getAccessToken(): Promise<string | null>;
  getTokens(): TokenSet | null;
  getClaims(): IdTokenClaims | null;
  /**
   * Clears the in-memory session and redirects to the identity provider's end-session endpoint (with
   * `id_token_hint` when a session is known). Resolves `true` when that redirect was started — the caller must not
   * navigate elsewhere — and `false` when there is no end-session endpoint.
   */
  logout(options?: { returnTo?: string }): Promise<boolean>;
  isAuthenticated(): boolean;
  dispose(): void;
  /** Listens to token changes (callback, refresh, logout). Returns an unsubscribe function. */
  subscribe(listener: OidcListener): () => void;
  /** Installs a token set directly (tests, session restore). */
  setTokens(tokens: TokenSet | null): void;
}



/** Error raised by the OIDC client; `code` follows OAuth error codes or `client.*` for local failures. */
export class OidcError extends Error {
  readonly code: string;
  readonly description?: string;
  /** The in-app location captured when the failed login started, so the caller can still honour it. */
  returnTo?: string;
  constructor(code: string, description?: string, returnTo?: string) {
    super(description ? `${code}: ${description}` : code);
    this.name = 'OidcError';
    this.code = code;
    this.description = description;
    if (returnTo !== undefined) this.returnTo = returnTo;
  }

  /** The identity provider has no usable session: the user must sign in interactively. Not a fault. */
  isInteractionRequired(): boolean {
    return ['login_required', 'interaction_required', 'consent_required', 'account_selection_required'].includes(this.code);
  }
}

/**
 * `authenticating`: a redirect callback or a silent restoration is in flight.
 * `expired`: the user was signed in, the session could not be renewed, and the screen is kept until they sign in again.
 */
export type AuthStatus = 'idle' | 'authenticating' | 'authenticated' | 'anonymous' | 'expired' | 'error';
